import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { PACKS, useShopPack, formatPackExpiry } from '../../hooks/useShopPack';
import {
  X, Check, Crown, Zap, Star, Gift,
  CreditCard, Calendar, ExternalLink, AlertCircle,
} from 'lucide-react';

/* ── Icônes par pack ── */
const PACK_ICONS = {
  free:    Gift,
  starter: Zap,
  premium: Star,
  pro:     Crown,
};

/* ── URL Worker (slash final retiré automatiquement) ── */
const WORKER_URL = (process.env.REACT_APP_WORKER_URL || '').replace(/\/$/, '');

/* ── Paiement via Worker Cloudflare ─────────────────────────────
   Le Worker appelle SlickPay côté serveur avec la clé SLICKPAY_API_KEY
   stockée dans ses variables d'environnement Cloudflare.
   → Aucune clé exposée dans le browser, pas de problème CORS.
────────────────────────────────────────────────────────────────── */
const initiatePayment = async ({ shopId, packId, period, merchantName, merchantEmail }) => {
  if (!WORKER_URL) {
    throw new Error('REACT_APP_WORKER_URL non défini dans .env.local');
  }

  const res = await fetch(`${WORKER_URL}/api/slickpay-create-invoice`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ shopId, packId, period, merchantName, merchantEmail }),
  });

  const data = await res.json();

  if (!res.ok || !data.success || !data.url) {
    throw new Error(data.error || 'Erreur lors de la création de la facture');
  }

  await updateDoc(doc(db, 'shops', shopId), {
    pendingPack:      packId,
    pendingPeriod:    period,
    pendingPaymentId: String(data.id),
    updatedAt:        serverTimestamp(),
  });

  window.location.href = data.url;
};

/* ════════════════════════════════════════════════════════════════ */
const PackModal = ({ shop, onClose }) => {
  const { pack: currentPack, isExpired, expiry } = useShopPack(shop);
  const [period,  setPeriod ] = useState('monthly');
  const [loading, setLoading] = useState(null);
  const [error,   setError  ] = useState(null);

  const handleSelectPack = useCallback(async (packId) => {
    if (packId === 'free') return;
    if (loading) return;
    setError(null);
    setLoading(packId);
    try {
      await initiatePayment({
        shopId:        shop.id,
        packId,
        period,
        merchantName:  shop.ownerName,
        merchantEmail: shop.email,
      });
    } catch (err) {
      setError(err.message || 'Une erreur est survenue. Réessayez.');
      setLoading(null);
    }
  }, [shop, period, loading]);

  const packList = Object.values(PACKS);

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.60)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'pkFadeIn 0.18s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--md-surface)',
          borderRadius: 'var(--md-radius-xl) var(--md-radius-xl) 0 0',
          width: '100%', maxWidth: 480, maxHeight: '92dvh', overflowY: 'auto',
          boxShadow: '0 -8px 40px rgba(0,0,0,0.30)',
          animation: 'pkSlideUp 0.25s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--md-outline)', margin: '0.75rem auto 0' }}/>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.25rem 0.75rem', borderBottom: '1px solid var(--md-outline)' }}>
          <div style={{ width: 38, height: 38, borderRadius: 'var(--md-radius-md)', background: 'rgba(0,201,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Crown size={20} color="#00c9a7" strokeWidth={2}/>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--md-text-primary)' }}>Choisir un pack</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--md-text-secondary)', marginTop: '0.1rem' }}>
              Pack actuel : <strong style={{ color: currentPack.color }}>{currentPack.name}</strong>
              {expiry && !isExpired && <span style={{ marginLeft: '0.4rem', color: 'var(--md-text-disabled)' }}>· {formatPackExpiry(expiry)}</span>}
              {isExpired && <span style={{ marginLeft: '0.4rem', color: '#ef4444' }}>· Expiré</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'var(--md-surface-2)', border: 'none', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--md-text-secondary)', flexShrink: 0 }}>
            <X size={14}/>
          </button>
        </div>

        {/* Toggle mensuel / annuel */}
        <div style={{ padding: '0.85rem 1.25rem 0' }}>
          <div style={{ display: 'inline-flex', background: 'var(--md-surface-2)', borderRadius: 'var(--md-radius-full)', padding: '0.2rem', border: '1px solid var(--md-outline)' }}>
            {['monthly', 'yearly'].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{ padding: '0.4rem 1rem', borderRadius: 'var(--md-radius-full)', border: 'none', background: period === p ? 'var(--md-primary)' : 'transparent', color: period === p ? '#fff' : 'var(--md-text-secondary)', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                {p === 'monthly'
                  ? <><CreditCard size={12}/> Mensuel</>
                  : <><Calendar size={12}/> Annuel <span style={{ fontSize: '0.65rem', background: '#00c9a7', color: '#fff', borderRadius: '999px', padding: '0.05rem 0.4rem', marginLeft: '0.2rem' }}>-17%</span></>
                }
              </button>
            ))}
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <div style={{ margin: '0.75rem 1.25rem 0', padding: '0.7rem 0.9rem', borderRadius: 'var(--md-radius-lg)', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem', color: '#ef4444', fontWeight: 500 }}>
            <AlertCircle size={14} style={{ flexShrink: 0 }}/> {error}
          </div>
        )}

        {/* Liste des packs */}
        <div style={{ padding: '0.85rem 1.25rem 2rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          {packList.map(pack => {
            const Icon      = PACK_ICONS[pack.id];
            const price     = period === 'yearly' ? pack.yearlyPrice : pack.monthlyPrice;
            const isCurrent = currentPack.id === pack.id;
            const isLoading = loading === pack.id;
            const isFree    = pack.id === 'free';
            return (
              <div key={pack.id} style={{ borderRadius: 'var(--md-radius-xl)', border: `2px solid ${isCurrent ? pack.color : 'var(--md-outline)'}`, background: isCurrent ? `${pack.color}08` : 'var(--md-surface-2)', overflow: 'hidden', transition: 'all 0.15s' }}>
                <div style={{ height: 3, background: pack.gradient }}/>
                <div style={{ padding: '0.85rem 1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 'var(--md-radius-sm)', background: `${pack.color}18`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={16} color={pack.color} strokeWidth={2}/>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--md-text-primary)' }}>
                        {pack.name}
                        {isCurrent && <span style={{ marginLeft: '0.4rem', fontSize: '0.62rem', background: pack.color, color: '#fff', borderRadius: '999px', padding: '0.1rem 0.5rem', fontWeight: 700, verticalAlign: 'middle' }}>Actuel</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {isFree
                        ? <div style={{ fontWeight: 800, fontSize: '1rem', color: pack.color }}>Gratuit</div>
                        : <><div style={{ fontWeight: 800, fontSize: '1rem', color: pack.color }}>{price.toLocaleString()} DA</div><div style={{ fontSize: '0.65rem', color: 'var(--md-text-disabled)', fontWeight: 500 }}>/{period === 'yearly' ? 'an' : 'mois'}</div></>
                      }
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.8rem' }}>
                    {pack.features.map((f, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.68rem', fontWeight: 600, color: 'var(--md-text-secondary)', background: 'var(--md-surface)', borderRadius: 'var(--md-radius-sm)', padding: '0.2rem 0.5rem' }}>
                        <Check size={9} color={pack.color} strokeWidth={3}/>{f}
                      </span>
                    ))}
                  </div>
                  {!isFree && (
                    <button onClick={() => handleSelectPack(pack.id)} disabled={!!loading || isCurrent} style={{ width: '100%', padding: '0.65rem', borderRadius: 'var(--md-radius-lg)', border: 'none', background: (loading || isCurrent) ? 'var(--md-outline)' : pack.gradient, color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: (loading || isCurrent) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.18s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem', boxShadow: (!loading && !isCurrent) ? `0 3px 12px ${pack.color}35` : 'none' }}>
                      {isLoading
                        ? <><div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'pkSpin 0.7s linear infinite' }}/> Redirection…</>
                        : isCurrent ? <><Check size={14}/> Pack actuel</>
                        : <><ExternalLink size={14}/> Choisir ce pack</>
                      }
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <p style={{ fontSize: '0.68rem', color: 'var(--md-text-disabled)', textAlign: 'center', lineHeight: 1.5, marginTop: '0.25rem' }}>
            Paiement sécurisé via SlickPay · CIB / Edahabia<br/>
            Vous serez redirigé vers la page de paiement SlickPay
          </p>
        </div>
      </div>
      <style>{`
        @keyframes pkFadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes pkSlideUp { from{transform:translateY(100%)} to{transform:translateY(0)} }
        @keyframes pkSpin    { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default PackModal;